import { codePaneFileKind, fileName } from '@/lib/notebook/fileTypes';
import { updateOverlayVisibility } from '@/lib/persistence';

export const CODE_PANE_ACTIVE_FILE_CHANGED_EVENT = 'antioch-code-pane-active-file-changed';
export const CODE_PANE_OPEN_FILE_EVENT = 'antioch-code-pane-open-file';
export const CODE_PANE_OPEN_FOLDER_EVENT = 'antioch-code-pane-open-folder';
export const CODE_PANE_OPEN_MEDIA_EVENT = 'antioch-code-pane-open-media';
export const CODE_PANE_FILE_CHANGED_EVENT = 'antioch-code-pane-file-changed';

export interface CodePaneFileEventDetail {
  path: string;
  source: string;
  pathCandidates?: string[];
}

export interface CodePaneFolderEventDetail {
  path: string;
  source: string;
  pathCandidates?: string[];
}

export interface CodePaneMediaEventDetail {
  name: string;
  path: string;
  src: string;
  mime: string;
  source: string;
}

export interface CodePaneOpenTabSyncState {
  path: string;
  isDirty: boolean;
}

export interface CodePaneFileChangePlan {
  changedOpenPaths: string[];
  dirtyChangedPaths: string[];
  activeChangedPath: string | null;
  reloadActivePath: string | null;
}

export function normalizeCodePanePath(path: string | null | undefined): string | null {
  if (!path) return null;
  const trimmed = path.trim();
  if (!trimmed) return null;

  const withoutHome = trimmed.startsWith('~/') ? trimmed.slice(2) : trimmed;
  const withoutRoot = withoutHome.startsWith('/root/') ? withoutHome.slice('/root/'.length) : withoutHome;
  const normalized = withoutRoot.replace(/^\.\/+/, '').replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/')) return null;
  if (!isSupportedCodePanePath(normalized)) return null;
  return normalized;
}

export function normalizeCodePaneDirectoryPath(path: string | null | undefined): string | null {
  if (path == null) return null;
  const trimmed = path.trim();
  if (!trimmed) return '';

  const withoutHome = trimmed.startsWith('~/') ? trimmed.slice(2) : trimmed;
  const withoutRoot = withoutHome.startsWith('/root/') ? withoutHome.slice('/root/'.length) : withoutHome;
  const normalized = withoutRoot
    .replace(/^\.\/+/, '')
    .replace(/\/{2,}/g, '/')
    .replace(/\/+$/, '');

  if (normalized === '.' || normalized === '/') return normalized === '.' ? '' : null;
  if (normalized.startsWith('/')) return null;
  return normalized;
}

export function codePanePathCandidates(path: string | undefined, cwd: string | null): string[] {
  const candidates: string[] = [];
  const normalized = normalizeCodePanePath(path);
  if (normalized) candidates.push(normalized);
  if (!path || !cwd) return Array.from(new Set(candidates));

  const trimmedPath = path.trim();
  const trimmedCwd = cwd.trim().replace(/\/+$/, '');
  if (!trimmedPath.startsWith('/')) {
    const cwdRelative = jupyterRelativeCwd(trimmedCwd);
    const cwdRelativePath = cwdRelative ? normalizeCodePanePath(`${cwdRelative}/${trimmedPath}`) : null;
    if (cwdRelativePath) candidates.push(cwdRelativePath);
  }
  if (trimmedPath.startsWith(`${trimmedCwd}/`)) {
    const cwdRelative = trimmedPath.slice(trimmedCwd.length + 1);
    const cwdRelativePath = normalizeCodePanePath(cwdRelative);
    const baseCwdRelative = jupyterRelativeCwd(trimmedCwd);
    const cwdNameRelativePath = baseCwdRelative ? normalizeCodePanePath(`${baseCwdRelative}/${cwdRelative}`) : null;
    if (cwdRelativePath) candidates.push(cwdRelativePath);
    if (cwdNameRelativePath) candidates.push(cwdNameRelativePath);
  }
  return Array.from(new Set(candidates));
}

export function codePaneDirectoryCandidates(path: string | null | undefined): string[] {
  const candidates: string[] = [];
  const normalized = normalizeCodePaneDirectoryPath(path);
  if (normalized !== null) candidates.push(normalized);

  const trimmed = path?.trim().replace(/\/+$/, '') ?? '';
  if (trimmed.startsWith('/') && normalized === null) {
    const parts = trimmed.split('/').filter(Boolean);
    for (let index = 0; index < parts.length; index += 1) {
      const suffixCandidate = normalizeCodePaneDirectoryPath(parts.slice(index).join('/'));
      if (suffixCandidate !== null) candidates.push(suffixCandidate);
    }
    candidates.push('');
  }

  return Array.from(new Set(candidates));
}

export function isSupportedCodePanePath(path: string | null | undefined): boolean {
  if (!path) return false;
  const name = fileName(path);
  return codePaneFileKind(path) !== null || !name.includes('.');
}

export function codePaneFileName(path: string): string {
  const normalized = normalizeCodePanePath(path) ?? path;
  return fileName(normalized);
}

export function announceCodePaneActiveFile(path: string, source: string): void {
  dispatchCodePaneEvent(CODE_PANE_ACTIVE_FILE_CHANGED_EVENT, path, source);
}

export function openCodePaneFile(path: string, source = 'agent', pathCandidates: string[] = []): boolean {
  const normalized = dispatchCodePaneEvent(CODE_PANE_OPEN_FILE_EVENT, path, source, pathCandidates);
  if (!normalized) return false;
  updateOverlayVisibility('notebook', true);
  return true;
}

export function openCodePaneFolder(path: string, source = 'agent', pathCandidates: string[] = []): boolean {
  const normalized = dispatchCodePaneFolderEvent(CODE_PANE_OPEN_FOLDER_EVENT, path, source, pathCandidates);
  if (normalized === null) return false;
  updateOverlayVisibility('notebook', true);
  return true;
}

export function openCodePaneMediaPreview(detail: {
  name: string;
  src: string;
  mime: string;
  source?: string;
  path?: string;
}): boolean {
  const name = detail.name.trim();
  const src = detail.src.trim();
  const mime = detail.mime.trim();
  if (!name || !src || !mime || typeof window === 'undefined') return false;
  const path = detail.path?.trim() || `agent-media:${stableMediaId(src)}:${name}`;
  window.dispatchEvent(
    new CustomEvent<CodePaneMediaEventDetail>(CODE_PANE_OPEN_MEDIA_EVENT, {
      detail: { name, path, src, mime, source: detail.source ?? 'agent' },
    })
  );
  updateOverlayVisibility('notebook', true);
  return true;
}

export function notifyCodePaneFileChanged(path: string, source = 'agent'): boolean {
  return dispatchCodePaneEvent(CODE_PANE_FILE_CHANGED_EVENT, path, source) !== null;
}

export function planCodePaneFileChange(
  candidates: string[],
  openTabs: CodePaneOpenTabSyncState[],
  activeFilePath: string | null,
  activeFileIsDirty: boolean
): CodePaneFileChangePlan {
  const candidateSet = new Set(candidates);
  const changedOpenPaths = openTabs.filter((tab) => candidateSet.has(tab.path)).map((tab) => tab.path);
  const activeChangedPath = activeFilePath && candidateSet.has(activeFilePath) ? activeFilePath : null;
  if (activeChangedPath && !changedOpenPaths.includes(activeChangedPath)) {
    changedOpenPaths.push(activeChangedPath);
  }

  const dirtyChangedPaths = openTabs.filter((tab) => candidateSet.has(tab.path) && tab.isDirty).map((tab) => tab.path);
  if (activeChangedPath && activeFileIsDirty && !dirtyChangedPaths.includes(activeChangedPath)) {
    dirtyChangedPaths.push(activeChangedPath);
  }

  const reloadActivePath =
    activeChangedPath && !dirtyChangedPaths.includes(activeChangedPath) ? activeChangedPath : null;

  return { changedOpenPaths, dirtyChangedPaths, activeChangedPath, reloadActivePath };
}

function stableMediaId(value: string): string {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function dispatchCodePaneEvent(
  type: string,
  path: string,
  source: string,
  pathCandidates: string[] = []
): string | null {
  const candidates = normalizeCandidatePaths(path, pathCandidates, normalizeCodePanePath);
  const normalized = candidates[0];
  if (!normalized || typeof window === 'undefined') return null;
  window.dispatchEvent(
    new CustomEvent<CodePaneFileEventDetail>(type, {
      detail: { path: normalized, source, pathCandidates: candidates },
    })
  );
  return normalized;
}

function dispatchCodePaneFolderEvent(
  type: string,
  path: string,
  source: string,
  pathCandidates: string[] = []
): string | null {
  const candidates = normalizeCandidatePaths(path, pathCandidates, normalizeCodePaneDirectoryPath);
  const normalized = candidates[0];
  if (normalized === undefined || typeof window === 'undefined') return null;
  window.dispatchEvent(
    new CustomEvent<CodePaneFolderEventDetail>(type, {
      detail: { path: normalized, source, pathCandidates: candidates },
    })
  );
  return normalized;
}

function normalizeCandidatePaths(
  path: string,
  pathCandidates: string[],
  normalize: (path: string | null | undefined) => string | null
): string[] {
  return Array.from(
    new Set(
      [path, ...pathCandidates].map((candidate) => normalize(candidate)).filter((candidate) => candidate !== null)
    )
  );
}

function jupyterRelativeCwd(cwd: string): string | null {
  const homeRelative = normalizeCodePaneDirectoryPath(cwd);
  if (homeRelative !== null && homeRelative !== '') return homeRelative;
  const parts = cwd.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? null;
}
